import { reactEvents } from '@bridge/reactEvents';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Grid,
  Heading,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { AddSupplier } from './add/AddSupplier';
import {
  Branch,
  VariantBranchSupplier,
  variantsApi,
} from '@texas/api/endpoints/variantsApi';
import { MirrorProps, SupplierCell } from './SupplierCell';
import { EditPurchaseInfoDrawer } from './EditPurchaseInfoDrawer';
import { DisableSupplierDialog } from './DisableSupplierDialog';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { LoadingOverlayV2 } from '@texas/components/shared/LoadingOverlayV2';
import { useTranslation } from 'react-i18next';
import { EnableSupplierDialog } from './EnableSupplierDialog';
import { ChooseContactsModal } from './ChooseContactsModal';
import { request } from '@texas/utils/helpers/httpHelpers';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { alphabet } from '@texas/utils/helpers/commonHelpers';
import { TFunction } from 'i18next';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { getBranchColor } from '@texas/utils/helpers/texas/branchHelpers';
import { FeatureGuard } from '@texas/components/shared/featureGuard/FeatureGuard';
import { useArticleContext } from '@texas/components/article/articleContext/useArticleContext';
import { TexasHeader } from '@texas/components/shared/TexasHeader';
import { productApprovalEvents } from '@texas/components/shared/productApproval/events';
import { inquiryEvents } from '@texas/components/article/variant/inquiry/events';
import { EntityDirectoryFilterBar } from '@texas/components/shared/entityDirectory/EntityDirectoryFilterBar';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { tagEvents } from '@texas/components/tags/events';
import { EntityType } from '@texas/api/endpoints/trackerApi';

interface LinkProps {
  branchId: number;
  supplierVariantId: number;
}

export function VariantSuppliers({
  articleId,
  variantId,
  pdVariant,
}: {
  articleId: number;
  variantId: number;
  pdVariant: boolean;
}) {
  const { t } = useTranslation();
  const articleContext = useArticleContext();
  const selectedBranchIds =
    articleContext.variantId === variantId ? articleContext.branchIds : [];
  const {
    data: suppliersOverview,
    refetch,
    loading,
  } = useApiResource(variantsApi.getVariantBranchSuppliers);

  const [mirrorData, setMirrorData] = useState<LinkProps | null>(null);
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const { request: createPurchaseInfoRequest } = useApiRequest(
    variantsApi.createPurchaseInformation,
  );

  const {
    value: editPurchaseInfoSupplier,
    isOpen: isEditPurchaseInfoOpen,
    onOpen: onEditPurchaseInfoOpen,
    onClose: onEditPurchaseInfoClose,
  } = useValueDisclosure<VariantBranchSupplier>();

  useEffect(() => {
    refetch(variantId, showInactive);
  }, [variantId, showInactive, refetch]);

  const reload = useCallback(() => {
    void refetch(variantId, showInactive);
  }, [refetch, showInactive, variantId]);

  useEffect(() => {
    return inquiryEvents.onVariantUpdated.subscribe((updatedVariantId) => {
      if (updatedVariantId === variantId) reload();
    });
  }, [reload, variantId]);

  useEffect(
    () =>
      tagEvents.tagLinksUpdated.subscribe((event) => {
        if (event.entityType === EntityType.Supplier) reload();
      }),
    [reload],
  );

  useSignalSubscriptionEvents({
    triggerOnEvent: reload,
    signalEvents: [
      tagEvents.tagUpdated,
      inquiryEvents.onUpdated,
      productApprovalEvents.productApprovalsCreated,
      productApprovalEvents.productApprovalChanged,
      productApprovalEvents.productApprovalRejected,
      productApprovalEvents.productApprovalAccepted,
      productApprovalEvents.productApprovalRevertedToPending,
    ],
  });

  const [addSupplierData, setAddSupplierData] = useState<{
    branchId: number;
    branchName: string;
    branchIdentifier: string;
  } | null>(null);
  const [isAddingToBranch, setIsAddingToBranch] = useState<number | null>(null);

  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    value: toMirror,
    isOpen: isOpenToMirror,
    onClose: onCloseToMirror,
    onOpen: onOpenToMirror,
  } = useValueDisclosure<number>();
  const {
    value: removeMirrorValue,
    isOpen: isOpenRemoveMirror,
    onClose: onCloseRemoveMirror,
    onOpen: onOpenRemoveMirror,
  } = useValueDisclosure<number>();

  const updateSupplierContactsRequest = (
    variantSupplierId: number,
    ids: number[],
  ) =>
    request(
      variantsApi.updateSupplierContacts,
      [variantSupplierId, { contactIds: ids }],
      () => {
        refetch(variantId, showInactive);
      },
    );

  const {
    value: disableSupplierValue,
    isOpen: isDisableSupplierOpen,
    onOpen: onDisableSupplierOpen,
    onClose: onDisableSupplierClose,
  } = useValueDisclosure<VariantBranchSupplier>();

  const {
    value: enableSupplierValue,
    isOpen: isEnableSupplierOpen,
    onOpen: onEnableSupplierOpen,
    onClose: onEnableSupplierClose,
  } = useValueDisclosure<VariantBranchSupplier>();

  const {
    value: editContactsValue,
    isOpen: isEditContactsOpen,
    onClose: onEditContactsClose,
    onOpen: onEditContactsOpen,
  } = useValueDisclosure<{ supplierId: number; variantSupplierId: number }>();

  const supplierLinks = useMemo(() => {
    const data: Record<number, { value: string } | undefined> = {};
    let i = 0;

    suppliersOverview?.suppliers.forEach((s) => {
      if (s.mirroringSuppliers.length === 0) return;

      data[s.id] = {
        value: alphabet.charAt(i),
      };

      i++;
    });

    return data;
  }, [suppliersOverview?.suppliers]);

  useEffect(() => {
    function onEscape(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isOpenToMirror) {
        setMirrorData(null);
      }
    }
    document.addEventListener('keyup', onEscape);

    return () => document.removeEventListener('keyup', onEscape);
  }, [isOpenToMirror]);

  const visibleBranches = suppliersOverview?.branches.filter(
    (branch) =>
      selectedBranchIds.length === 0 || selectedBranchIds.includes(branch.id),
  );
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const suppliers = (suppliersOverview?.suppliers ?? []).filter((supplier) =>
    supplier.supplierName.toLocaleLowerCase().includes(normalizedQuery),
  );

  return (
    <BodyOverride>
      {disableSupplierValue && (
        <DisableSupplierDialog
          onDisable={() => {
            refetch(variantId, showInactive);
            reactEvents.variantSupplierChanged.dispatch();
          }}
          isOpen={isDisableSupplierOpen}
          onClose={onDisableSupplierClose}
          supplier={disableSupplierValue}
        />
      )}
      {enableSupplierValue && (
        <EnableSupplierDialog
          onEnable={() => {
            refetch(variantId, showInactive);
            reactEvents.variantSupplierChanged.dispatch();
          }}
          isOpen={isEnableSupplierOpen}
          onClose={onEnableSupplierClose}
          supplier={enableSupplierValue}
        />
      )}

      <Drawer isOpen={isOpen} onClose={onClose} size="sm">
        <DrawerOverlay />

        <DrawerContent bg="texas.bg.900" _light={{ bg: 'gray.10' }}>
          <DrawerCloseButton />
          {addSupplierData && (
            <AddSupplier
              variantId={variantId}
              isPdVariant={true}
              branchId={addSupplierData.branchId}
              branchName={addSupplierData.branchName}
              branchIdentifier={addSupplierData.branchIdentifier}
              setIsAddingToBranch={setIsAddingToBranch}
            />
          )}
        </DrawerContent>
      </Drawer>

      <Box mb={4}>
        <TexasHeader text={t('navigation.suppliers')} />
        <Text color="entityDirectory.mutedText" fontSize="sm" mt={1}>
          {t('variant.supplier.pageDescription')}
        </Text>
      </Box>

      <Collapse in={mirrorData !== null}>
        <Box>
          <Heading>{t('variant.supplier.mirrorMode')}</Heading>
          <Text>{t('variant.supplier.mirrorModeDesc')}</Text>
          <Button
            onClick={() => setMirrorData(null)}
            mb="4"
            mt={2}
            rightIcon={
              <Icons.KeyboardEscape opacity={0.7} boxSize={defaultIconSize} />
            }
          >
            {t('general.discard')}
          </Button>
        </Box>
      </Collapse>

      <Box bg="entityDirectory.canvas" p={4}>
        <EntityDirectoryFilterBar
          actions={
            <Box flexShrink={0}>
              <TexasSwitch
                checked={showInactive}
                label={t('suppliers.showInactive')}
                onChange={setShowInactive}
              />
            </Box>
          }
          mb={4}
        >
          <Box flex="1 1 280px" maxW={{ base: 'full', lg: '340px' }}>
            <SearchFilterInput
              style="entityDirectory"
              value={query}
              onChange={setQuery}
              placeholder={t('suppliers.searchSuppliers')}
            />
          </Box>
        </EntityDirectoryFilterBar>

        {loading && (
          <Box minH="100px">
            <LoadingOverlayV2 loaded={!loading} label={t('general.loading')} />
          </Box>
        )}

        {!loading && (
          <Box overflowX="auto">
            <Grid
              templateColumns={`repeat(${
                visibleBranches?.length ?? 0
              }, minmax(340px, 420px))`}
              minW={`${Math.max(visibleBranches?.length ?? 0, 1) * 352}px`}
              gap={3}
              justifyContent="start"
            >
              {visibleBranches?.map((b) => {
                const branchSuppliers = suppliers.filter(
                  (x) => x.branchId === b.id,
                );

                return (
                  <Flex key={b.id} direction="column" minH="440px">
                    <Flex
                      align="center"
                      justify="space-between"
                      px={4}
                      h="76px"
                      bg="entityDirectory.surface"
                      borderBottom="1px solid"
                      borderColor="entityDirectory.border"
                    >
                      <Box>
                        <Flex gap={2} align="center">
                          <Text fontWeight="bold" fontSize="lg">
                            {b.identifier}
                          </Text>
                          <Box boxSize={2} bg={getBranchColor(b.identifier)} />
                        </Flex>
                        <Text color="entityDirectory.mutedText" fontSize="xs">
                          {b.name}
                        </Text>
                      </Box>
                      <Box textAlign="right">
                        <Text fontSize="lg" fontWeight="bold">
                          {branchSuppliers.length}
                        </Text>
                        <Text
                          color="entityDirectory.mutedText"
                          fontSize="2xs"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="wider"
                        >
                          {t('suppliers.supplierCount', {
                            count: branchSuppliers.length,
                          })}
                        </Text>
                      </Box>
                    </Flex>
                    <Button
                      variant="ghost"
                      borderBottom="1px solid"
                      borderColor="entityDirectory.border"
                      isDisabled={mirrorData !== null}
                      onClick={() => {
                        setAddSupplierData({
                          branchId: b.id,
                          branchName: b.name,
                          branchIdentifier: b.identifier,
                        });
                        onOpen();
                      }}
                      isLoading={isAddingToBranch === b.id}
                    >
                      <Icons.Plus boxSize={4} mr={2} />
                      {t('suppliers.addSupplier')}
                    </Button>
                    <Flex direction="column" gap={3} p={3}>
                      {branchSuppliers.map((x) => {
                        const {
                          tooltipLabel,
                          isMirrorSelf,
                          mirrorDisabled,
                          mirrorValue,
                        } = mirrorState(t, mirrorData, x, b, supplierLinks);

                        return (
                          <Tooltip key={x.supplierId} label={tooltipLabel}>
                            <Box
                              transition="opacity 200ms ease, transform 200ms ease"
                              border={
                                isMirrorSelf ? '1px solid white' : undefined
                              }
                              {...(mirrorDisabled
                                ? {
                                    opacity: 0.5,
                                    transform: 'scale(0.95)',
                                  }
                                : null)}
                              _hover={{
                                ...(mirrorData &&
                                !mirrorDisabled &&
                                !isMirrorSelf
                                  ? {
                                      transform: 'scale(1.06)',
                                      cursor: 'pointer',
                                    }
                                  : undefined),
                              }}
                            >
                              <SupplierCell
                                articleId={articleId}
                                enableMirror={pdVariant}
                                isProductDevelopment={pdVariant}
                                mirrorValue={mirrorValue}
                                mirrorModeOnClick={
                                  mirrorData
                                    ? () => {
                                        if (mirrorDisabled || isMirrorSelf)
                                          return;
                                        onOpenToMirror(x.id);
                                      }
                                    : undefined
                                }
                                onMirrorClick={() => {
                                  if (x.mirrorSupplier) {
                                    onOpenRemoveMirror(x.id);
                                    return;
                                  }
                                  setMirrorData({
                                    branchId: x.branchId,
                                    supplierVariantId: x.id,
                                  });
                                }}
                                supplier={x}
                                onDisable={() => {
                                  onDisableSupplierOpen(x);
                                }}
                                onEnable={() => {
                                  onEnableSupplierOpen(x);
                                }}
                                onChooseContacts={() =>
                                  onEditContactsOpen({
                                    supplierId: x.supplierId,
                                    variantSupplierId: x.id,
                                  })
                                }
                                onCreatePurchaseInfo={async () => {
                                  const res = await createPurchaseInfoRequest(
                                    variantId,
                                    {
                                      supplierId: x.supplierId,
                                      branchId: x.branchId,
                                    },
                                  );
                                  if (!res.hasError)
                                    refetch(variantId, showInactive);
                                }}
                                onEditPurchaseInfo={() =>
                                  onEditPurchaseInfoOpen(x)
                                }
                              />
                            </Box>
                          </Tooltip>
                        );
                      })}
                      {isAddingToBranch === b.id && (
                        <Flex gap={2} px={2} alignItems="center">
                          <Spinner size="sm" />
                          <Text>{t('suppliers.addingSupplier')}</Text>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                );
              })}
            </Grid>
          </Box>
        )}
      </Box>
      {editContactsValue && (
        <ChooseContactsModal
          acceptLabel={t('suppliers.updateContacts')}
          variantSupplierId={editContactsValue.variantSupplierId}
          supplierId={editContactsValue.supplierId}
          onContactsSelect={(ids) => {
            updateSupplierContactsRequest(
              editContactsValue.variantSupplierId,
              ids,
            );
            onEditContactsClose();
          }}
          isOpen={isEditContactsOpen}
          onClose={onEditContactsClose}
        />
      )}
      {toMirror && mirrorData && (
        <VerifyDialogWithRequest
          headerTitle={t('variant.supplier.mirrorEnableTitle')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          primaryRequest={variantsApi.mirrorSupplier}
          args={[mirrorData.supplierVariantId, toMirror]}
          isOpen={isOpenToMirror}
          onClose={onCloseToMirror}
          onPerformed={() => {
            refetch(variantId, showInactive);
            setMirrorData(null);
          }}
          onSuccessTitle={t('variant.supplier.mirrorSuccess')}
        >
          {t('alert.areYouSure')}
        </VerifyDialogWithRequest>
      )}
      {removeMirrorValue && (
        <VerifyDialogWithRequest
          headerTitle={t('variant.supplier.mirrorDisableTitle')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          primaryRequest={variantsApi.removeMirror}
          args={[removeMirrorValue]}
          isOpen={isOpenRemoveMirror}
          onClose={onCloseRemoveMirror}
          onPerformed={() => {
            refetch(variantId, showInactive);
          }}
          onSuccessTitle={t('variant.supplier.mirrorDisabledSuccess')}
        >
          {t('alert.areYouSure')}
        </VerifyDialogWithRequest>
      )}
      {editPurchaseInfoSupplier?.purchaseInformationId && (
        <FeatureGuard hideBadge={true} optOut={!pdVariant}>
          <EditPurchaseInfoDrawer
            isOpen={isEditPurchaseInfoOpen}
            onClose={onEditPurchaseInfoClose}
            purchaseInformationId={
              editPurchaseInfoSupplier.purchaseInformationId
            }
            currencyCode={editPurchaseInfoSupplier.currencyCode ?? ''}
            exchangeRate={editPurchaseInfoSupplier.exchangeRate ?? 0}
            branchId={editPurchaseInfoSupplier.branchId}
            supplierId={editPurchaseInfoSupplier.supplierId}
            supplierName={editPurchaseInfoSupplier.supplierName}
            onSaved={() => refetch(variantId, showInactive)}
          />
        </FeatureGuard>
      )}
    </BodyOverride>
  );
}

function mirrorState(
  t: TFunction,
  mirrorData: LinkProps | null,
  supplier: VariantBranchSupplier,
  branch: Branch,
  supplierLinks: Record<number, { value: string } | undefined>,
) {
  const mirrorDisabled =
    (mirrorData?.branchId === branch.id &&
      mirrorData.supplierVariantId !== supplier.id) ||
    (mirrorData && supplier.mirrorSupplier) ||
    supplier.disabled;
  const isMirrorSelf = mirrorData?.supplierVariantId === supplier.id;

  let tooltipLabel: string | undefined = undefined;
  const mirrorValue: MirrorProps | null = supplier.mirrorSupplier
    ? {
        type: 'follower',
        value: supplierLinks[supplier.mirrorSupplier.id]!.value,
      }
    : supplierLinks[supplier.id] !== undefined
    ? { type: 'main', value: supplierLinks[supplier.id]!.value }
    : null;

  if (mirrorData) {
    if (isMirrorSelf) {
      tooltipLabel = t('variant.supplier.notMirrorSelf');
    } else if (mirrorDisabled) {
      tooltipLabel = t('variant.supplier.notMirrorSupplier');
    } else {
      tooltipLabel = t('variant.supplier.mirrorSupplierDesc');
    }
  }

  return { mirrorDisabled, isMirrorSelf, mirrorValue, tooltipLabel };
}
